import { QuestionResponseFormat } from '@mygpt/app/chat/constant';
import { Http } from '@mygpt/app/http';
import { PageInfo, ratingType, RecommendList, ReferenceSign } from '@mygpt/types';

export * from './session';

export interface questionsParams {
  ai_id: string;
  session_id: string;
  message_id: string;
  question: string;
  stream: boolean;
  debug_with_messages?: boolean; // 是否启用显示prompt，注意：需要设置stream为false
  max_tokens?: number; // 设置回答最大字数限制
  simple_question?: boolean; // 简易模式（速度快），复制模式（速度慢但更详细）
  with_source?: boolean; // 是否显示来源
  needToken?: boolean; // 是否带token，历史记录需要
  format?: QuestionResponseFormat; // 返回格式
  page_info?: { title: string; url: string }; // 页面信息
  created_by?: string; // 用来标识是哪个用户创建的，未登录情况下有效，如果不传则使用ip判断
  // question接口需要的动态背景信息，用来给Agent类型的Bot背后的Agent LLM理解用。
  // 这个参数的文档：https://vo216btkxyi.jp.larksuite.com/wiki/Tuj0wbxamiZbDwkVHDujCeWqpwd
  dynamic_background?: Array<{
    description: string; // 必填字段，用于描述该组动态背景信息的作用
    [key: string]: unknown; // 其他任意数量字段，任意值
  }>;
  language?: string; // 语言，en（英文），zh（中文），ja（日文），ko（韩文）
  agentMode?: string; // 模式，kiosk（大屏模式）
  user_identifier?: string; // 用户昵称
}

export type feedbackType = 'good' | 'bad';

export interface feedbackMetadata {
  type: feedbackType;
  value: string[];
}

export interface messageRatingParams {
  ai_id: string;
  session_id: string;
  message_id: string;
  rating: ratingType;

  feedback_content?: string;
  feedback_metadata: feedbackMetadata;
}

export interface metricsViewParams extends PageInfo {
  ai_id: string;
}

/**
 * 机器人问答
 * @param params
 * @returns
 */
export const questions = (params: questionsParams, signal: AbortSignal) => {
  const { fetch } = Http.use();
  return fetch(`/questions/${params.ai_id}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      notNeedToken: params.needToken ? 'false' : 'true',
    },
    body: JSON.stringify(params),
    signal: signal,
  });
};

/**
 * 回答点赞
 * @param params
 * @returns
 */
export const messageRating = (params: messageRatingParams) => {
  const { patch } = Http.use();
  const { ai_id, message_id, rating, session_id, ...rest } = params;
  return patch(`/v1/${ai_id}/message_rating?session_id=${session_id}&message_id=${message_id}&rating=${rating}`, rest);
};

///questions/references/{ai_id}?message_id=
export const referencesSSE = (
  ai_id: string,
  message_id: string,
  callback: (done: boolean, params?: { type: string; data: number }) => void
) => {
  const { fetch } = Http.use();
  return fetch(`/questions/references/${ai_id}?message_id=${message_id}`).then((response) => {
    const handleResponse = async () => {
      const reader = response.body?.getReader();
      if (!reader) return;
      const decoder = new TextDecoder();
      let done = false;
      while (!done) {
        const { done: doneReading, value } = await reader.read();
        done = doneReading;
        const chunkValue = decoder.decode(value);
        try {
          chunkValue.split('\n').forEach((item) => {
            if (!item) return;
            callback(false, JSON.parse(item));
          });
          if (done) {
            callback(true);
          }
        } catch (error) {
          console.error(`Failed to parse chunkValue(${chunkValue}) error ${error}`, error);
        }
      }
    };
    handleResponse();
    return response;
  });
};

export const getReferencesService = (ai_id: string, message_id: string) => {
  const { get } = Http.use();
  return get<ReferenceSign[]>(`/questions/references/${ai_id}?message_id=${message_id}`);
};

/**
 * faq 相关问题 文字翻译
 * @param params
 * @returns
 */
export interface faqRecommendTranslationParams {
  ai_id: string;
  recommend_list: {
    id: string;
    name: string;
    language: string;
  }[];
}
export const faqRecommendTranslation = (params: faqRecommendTranslationParams) => {
  const { ai_id, ...rest } = params;
  const { post } = Http.use();
  return post<RecommendList[]>(`/v1/faq_recommend_translation/${ai_id}`, rest);
};

/**
 * 返回文件地址文件
 */
export interface getEmbeddingFileParams {
  file_id: string;
  to_pdf?: boolean;
}
export const getEmbeddingFile = (params: getEmbeddingFileParams) => {
  const { file_id, to_pdf } = params;
  const { defaults } = Http.use();
  return `${defaults.baseURL}/embeddings/files/${file_id}/download${to_pdf ? '?to_pdf=true' : ''}`;
};

export const signatureClickAction = (params: { session_id: string; ai_id: string; icon: string; content: string }) => {
  const { post } = Http.use();
  return post(`/session/metrics/${params.ai_id}/click/signature`, {
    session_id: params.session_id,
    icon: params.icon,
    content: params.content,
  });
};

export interface SearchQuestionServiceParams {
  aiId: string;
  search: string;
}

export interface SearchQuestionItem {
  id: string;
  question: string;
  score?: number;
}

export const searchQuestionService = (params: SearchQuestionServiceParams) => {
  const { aiId, search } = params;

  const { get } = Http.use();
  return get<SearchQuestionItem[]>(`/questions/${aiId}/search?query=${search}`);
};

export const transferToHumanAction = (params: { ai_id: string; message_id: string }) => {
  const { ai_id, message_id } = params;
  const { post } = Http.use();
  return post(`/questions/${ai_id}/message_to_human/${message_id}`);
};
